import { formatMoney } from '@aicall/shared';

/** All display formatting in one place, so every page reads numbers the same way. */
export const money = (micros: number | undefined | null, currency = 'USD') => formatMoney(micros ?? 0, currency);
export const moneyPrecise = (micros: number | undefined | null, currency = 'USD') => formatMoney(micros ?? 0, currency, { precise: true });

export const num = (n: number | undefined | null, digits = 0) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(n ?? 0);

/** Whole minutes left from a balance kept in seconds. */
export const minutesLeft = (seconds: number | undefined | null) => num(Math.max(0, Math.floor((seconds ?? 0) / 60)));

export const compact = (n: number | undefined | null) => new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n ?? 0);

export const pct = (part: number, whole: number, digits = 0) => (whole > 0 ? `${((part / whole) * 100).toFixed(digits)}%` : '—');

export function duration(seconds: number | undefined | null): string {
  const s = Math.round(seconds ?? 0);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

export function dateTime(value: string | Date | undefined | null): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(value));
}

export function date(value: string | Date | undefined | null): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(value));
}

export function relative(value: string | Date | undefined | null): string {
  if (!value) return '—';
  const diff = (Date.now() - new Date(value).getTime()) / 1000;
  if (Math.abs(diff) < 45) return 'just now';
  const units: [number, Intl.RelativeTimeFormatUnit][] = [[60, 'second'], [60, 'minute'], [24, 'hour'], [7, 'day'], [4.35, 'week'], [12, 'month'], [Infinity, 'year']];
  let v = diff;
  let unit: Intl.RelativeTimeFormatUnit = 'second';
  for (const [step, u] of units) {
    unit = u;
    if (Math.abs(v) < step) break;
    v /= step;
  }
  return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(-Math.round(v), unit);
}

/** +14165550199 -> (416) 555-0199 */
export function phone(e164: string | undefined | null): string {
  if (!e164) return '—';
  if (/^\+1\d{10}$/.test(e164)) return `(${e164.slice(2, 5)}) ${e164.slice(5, 8)}-${e164.slice(8)}`;
  return e164;
}

export const titleCase = (key: string | undefined | null) =>
  (key ?? '').toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const dayLabel = (day: string) => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${day}T00:00:00Z`));

export const initials = (first?: string, last?: string, fallback = '?') => ((first?.[0] ?? '') + (last?.[0] ?? '')).toUpperCase() || fallback;

/** A ledger quantity in the unit a person counts in: 0.0177 "1M tokens" -> "17,700 tokens". */
export function usageQuantity(quantity: number, unit: string): string {
  if (unit === '1M tokens') return `${num(quantity * 1e6)} tokens`;
  if (unit === '1K chars') return `${num(quantity * 1000)} chars`;
  if (unit === 'minute') return `${num(quantity, 2)} min`;
  if (unit === 'message') return `${num(quantity)} msg`;
  if (unit === 'month') return `${num(quantity)} mo`;
  return `${num(quantity, 4)} ${unit}`;
}
